/**
 * pickedProductCache — the PICKED products the canvas already resolved, kept across reloads, SCOPED BY STORE.
 *
 * Why it exists (founder report 2026-09-17, "Produit à la une"): a section bound to a real product still
 * opened on the built-in SAMPLE product. `usePickedProducts` resolves `{kind:'product', id}` refs with a
 * `GET /api/products/{id}` fired from an effect, so the FIRST paint always had an empty `resolved.products`
 * map — the canvas painted "Linen Weekend Shirt / $249" for as long as the round trip took, and the picker
 * trigger printed a bare "…". On a cold load that is the first thing the seller sees, and it reads as "the
 * builder lost my product". Same defect the preview product had, and the same cure: stash the resolved
 * product so the next open hydrates SYNCHRONOUSLY from cache and revalidates in the background
 * (see `readPersistedPreviewProduct` in builderStore.ts).
 *
 * Contract:
 *  - Key namespaced by the JWT `store_id` claim, so an agency editing several stores never restores store
 *    A's product onto store B (`getStoreId` is a read-only scoping convenience — the backend stays the
 *    tenancy authority).
 *  - Capped at {@link MAX_ENTRIES} (insertion order, oldest evicted) — a page can reference a dozen products
 *    and a full `StorefrontProduct` is a few KB; the cache must never grow into the storage quota.
 *  - Best-effort throughout: private mode, a disabled/full storage or a corrupt entry all degrade to "no
 *    cache" (the old behaviour), never to a throw. The canvas is not allowed to break over a cache.
 *  - A snapshot can be STALE (renamed, unpublished, deleted). It is only ever a first-paint stand-in:
 *    `usePickedProducts` re-fetches every referenced id once per session and {@link forgetPickedProduct}s the
 *    ones the server no longer serves, so a deleted product survives at most one paint.
 */
import type { StorefrontProduct } from '@aquilixshop/ui-react';
import { getStoreId } from '../boot/session';
import type { PickerItem } from '../inspector/controls/EntityPickerListbox';

const KEY_BASE = 'aqx-builder-picked-products';
const MAX_ENTRIES = 12;

function storageKey(): string {
  return `${KEY_BASE}:${getStoreId() ?? 'default'}`;
}

function storage(): Storage | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    return null; // storage disabled (private mode / blocked cookies)
  }
}

/** The snapshot as written: id → product, in insertion order (JSON objects preserve string-key order). */
type Snapshot = Record<string, StorefrontProduct>;

function readSnapshot(): Snapshot {
  const store = storage();
  if (!store) return {};
  try {
    const raw = store.getItem(storageKey());
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    const out: Snapshot = {};
    for (const [id, product] of Object.entries(parsed as Snapshot)) {
      // Guard the shape: a half-written or older entry must not reach the renderer as a product.
      if (product && typeof product.id === 'string' && typeof product.name === 'string')
        out[id] = product;
    }
    return out;
  } catch {
    return {};
  }
}

function writeSnapshot(snapshot: Snapshot): void {
  const store = storage();
  if (!store) return;
  try {
    const entries = Object.entries(snapshot);
    const capped =
      entries.length > MAX_ENTRIES ? entries.slice(entries.length - MAX_ENTRIES) : entries;
    if (capped.length === 0) store.removeItem(storageKey());
    else store.setItem(storageKey(), JSON.stringify(Object.fromEntries(capped)));
  } catch {
    // Quota exceeded / storage disabled → the next open simply pays for the round trip again.
  }
}

/** Every remembered product for this store — the seed `usePickedProducts` paints from before its fetch. */
export function readPickedProducts(): Snapshot {
  return readSnapshot();
}

/** Remember (or refresh) one resolved product. Re-inserting moves it to the newest slot. */
export function rememberPickedProduct(product: StorefrontProduct): void {
  const snapshot = readSnapshot();
  delete snapshot[product.id];
  snapshot[product.id] = product;
  writeSnapshot(snapshot);
}

/** Drop one id — the server no longer serves it (deleted, or no longer ACTIVE ⇒ the shop hides it). */
export function forgetPickedProduct(id: string): void {
  const snapshot = readSnapshot();
  if (!(id in snapshot)) return;
  delete snapshot[id];
  writeSnapshot(snapshot);
}

/**
 * The picker ROW for an id, straight from the cache — no request, no await. It is what lets the inspector
 * trigger name the bound product on the first frame instead of printing "…" until a list request lands.
 * The thumbnail mirrors `coverOf`: the first PUBLIC photo, the one the shop shows.
 */
export function peekPickedItem(id: string): PickerItem | null {
  const product = readSnapshot()[id];
  if (!product) return null;
  return { id: product.id, name: product.name, thumbnailUrl: product.images?.[0]?.url ?? null };
}
